/**
 * Componente: Diseñador — biblioteca de diseños de marca por municipio
 * Hasta N diseños por municipio (N = limite_disenos del backend: el general o
 * uno propio del cliente), cada uno con logo + colores + config de impresión +
 * canvas de posiciones; uno activo. "Editar" abre la pantalla única de DisenadorEditor
 * (modo="editar"): nombre/colores/canvas, todo junto.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DisenadorGaleria from './DisenadorGaleria';
import DisenadorEditor from './DisenadorEditor';
import ConfiguracionImpresionModal from './ConfiguracionImpresionModal';
import ConfirmarEliminarDisenoModal from './ConfirmarEliminarDisenoModal';
import { IconAdvertencia } from './icons';
import '../styles/padron.css';
import '../styles/disenador.css';

const API_URL = process.env.REACT_APP_API_URL;

// 'galeria' | 'nuevo-config-impresion' | 'nuevo-editor' | 'editar'
const Disenador = ({ resetSignal, onVolverDashboard }) => {
  const [disenos, setDisenos] = useState([]);
  // Tope de diseños de este municipio (lo manda el backend); null hasta que carga.
  const [limiteDisenos, setLimiteDisenos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [pantalla, setPantalla] = useState('galeria');
  const [disenoEditando, setDisenoEditando] = useState(null);
  const [nuevoTipoImpresion, setNuevoTipoImpresion] = useState('');
  const [nuevoTipoTarjeta, setNuevoTipoTarjeta] = useState('');
  const [procesandoId, setProcesandoId] = useState(null);
  const [disenoAEliminar, setDisenoAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('token')}` });

  const cargarDisenos = useCallback(async () => {
    try {
      setCargando(true);
      setError('');
      const { data } = await axios.get(`${API_URL}/municipios/disenos`, { headers: authHeaders() });
      setDisenos(data.disenos || []);
      setLimiteDisenos(Number.isInteger(data.limite_disenos) ? data.limite_disenos : null);
    } catch (err) {
      console.error('Error al cargar los diseños:', err);
      setError(err.response?.data?.mensaje || 'Error al cargar los diseños');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargarDisenos();
  }, [cargarDisenos]);

  // Municipio nuevo sin ningún diseño todavía: directo al flujo de creación,
  // sin pasar por una galería vacía.
  useEffect(() => {
    if (!cargando && disenos.length === 0 && pantalla === 'galeria') {
      setPantalla('nuevo-config-impresion');
    }
  }, [cargando, disenos, pantalla]);

  // Cada click en "Diseñador" del sidebar (incluso estando ya en esta
  // sección) cambia resetSignal. Si no reseteamos acá explícitamente, un
  // click estando en pantalla "editar"/"crear" no hace nada visible: como
  // seccionActiva en Dashboard.jsx no cambia (ya era 'diseñador'), React no
  // vuelve a montar este componente solo, así que el estado interno
  // (pantalla, disenoEditando) queda pegado donde estaba.
  useEffect(() => {
    setPantalla('galeria');
    setDisenoEditando(null);
  }, [resetSignal]);

  const irAGaleria = async () => {
    await cargarDisenos();
    setPantalla('galeria');
  };

  const abrirNuevo = () => {
    setNuevoTipoImpresion('');
    setNuevoTipoTarjeta('');
    setPantalla('nuevo-config-impresion');
  };

  const alElegirConfigNuevo = (tipoImpresion, tipoTarjeta) => {
    setNuevoTipoImpresion(tipoImpresion);
    setNuevoTipoTarjeta(tipoTarjeta);
    setPantalla('nuevo-editor');
  };

  const abrirEditar = (diseno) => {
    setDisenoEditando(diseno);
    setPantalla('editar');
  };

  const activar = async (id) => {
    try {
      setProcesandoId(id);
      setError('');
      await axios.put(`${API_URL}/municipios/disenos/${id}/activar`, {}, { headers: authHeaders() });
      await cargarDisenos();
    } catch (err) {
      console.error('Error al activar el diseño:', err);
      setError(err.response?.data?.mensaje || 'Error al activar el diseño');
    } finally {
      setProcesandoId(null);
    }
  };

  const duplicar = async (id) => {
    try {
      setProcesandoId(id);
      setError('');
      await axios.post(`${API_URL}/municipios/disenos/${id}/duplicar`, {}, { headers: authHeaders() });
      await cargarDisenos();
    } catch (err) {
      console.error('Error al duplicar el diseño:', err);
      setError(err.response?.data?.mensaje || 'Error al duplicar el diseño');
    } finally {
      setProcesandoId(null);
    }
  };

  const pedirConfirmacionEliminar = (diseno) => {
    setDisenoAEliminar(diseno);
  };

  const confirmarEliminar = async () => {
    if (!disenoAEliminar) return;

    try {
      setEliminando(true);
      setError('');
      await axios.delete(`${API_URL}/municipios/disenos/${disenoAEliminar.id}`, { headers: authHeaders() });
      setDisenoAEliminar(null);
      await cargarDisenos();
    } catch (err) {
      console.error('Error al eliminar el diseño:', err);
      setError(err.response?.data?.mensaje || 'Error al eliminar el diseño');
    } finally {
      setEliminando(false);
    }
  };

  if (cargando) {
    return (
      <section className="disenador-section">
        <div className="padron-loading">
          <div className="loading-spinner"></div>
          <p>Cargando diseños...</p>
        </div>
      </section>
    );
  }

  if (pantalla === 'nuevo-config-impresion') {
    return (
      <section className="disenador-section">
        {disenos.length === 0 && (
          <div className="padron-empty">
            <p>Configurá tu primer diseño para continuar.</p>
          </div>
        )}
        <ConfiguracionImpresionModal
          valorInicial={null}
          obligatorio={disenos.length === 0}
          onClose={() => setPantalla('galeria')}
          onSeleccionar={alElegirConfigNuevo}
        />
      </section>
    );
  }

  if (pantalla === 'nuevo-editor') {
    return (
      <DisenadorEditor
        modo="crear"
        tipoImpresionInicial={nuevoTipoImpresion}
        tipoTarjetaInicial={nuevoTipoTarjeta}
        onGuardado={irAGaleria}
        onCancelar={irAGaleria}
      />
    );
  }

  if (pantalla === 'editar') {
    return (
      <DisenadorEditor
        modo="editar"
        disenoInicial={disenoEditando}
        onGuardado={irAGaleria}
        onCancelar={irAGaleria}
      />
    );
  }

  return (
    <>
      {error && (
        <div className="alert alert-error">
          <IconAdvertencia size={18} />
          <p>{error}</p>
        </div>
      )}
      <DisenadorGaleria
        disenos={disenos}
        limite={limiteDisenos}
        onNuevo={abrirNuevo}
        onEditar={abrirEditar}
        onActivar={activar}
        onDuplicar={duplicar}
        onEliminar={pedirConfirmacionEliminar}
        procesandoId={procesandoId}
        onVolverDashboard={onVolverDashboard}
      />

      {disenoAEliminar && (
        <ConfirmarEliminarDisenoModal
          nombre={disenoAEliminar.nombre}
          eliminando={eliminando}
          onConfirmar={confirmarEliminar}
          onCancelar={() => setDisenoAEliminar(null)}
        />
      )}
    </>
  );
};

export default Disenador;
